import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  idpLabel,
  type AssessorAssignment,
  type CreatedInvitation,
  type Invitation,
  type OrgMember,
  type OrgRole,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useAuth, useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  formatDate,
  useToast,
} from '../../components/ui.js';

const ROLES: OrgRole[] = ['admin', 'contributor', 'reviewer'];

export function MembersPage() {
  const { org } = useOrg();
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isAdmin = org.role === 'admin';

  const members = useQuery({
    queryKey: ['members', org.orgId],
    queryFn: () => apiFetch<OrgMember[]>(`/orgs/${org.orgId}/members`),
    enabled: isAdmin,
  });
  const invitations = useQuery({
    queryKey: ['invitations', org.orgId],
    queryFn: () => apiFetch<Invitation[]>(`/orgs/${org.orgId}/invitations`),
    enabled: isAdmin,
  });
  const assessors = useQuery({
    queryKey: ['assessors', org.orgId],
    queryFn: () => apiFetch<AssessorAssignment[]>(`/orgs/${org.orgId}/assessors`),
    enabled: isAdmin,
  });

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['members', org.orgId] });
    void queryClient.invalidateQueries({ queryKey: ['invitations', org.orgId] });
    void queryClient.invalidateQueries({ queryKey: ['assessors', org.orgId] });
  };

  const onError = (err: unknown) => {
    if (err instanceof ApiRequestError && err.code === 'payment_required') {
      toast('Plan limit reached — upgrade on the Billing page to add more people.', 'error');
    } else {
      toast(err instanceof ApiRequestError ? err.message : 'Something went wrong', 'error');
    }
  };

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgRole>('contributor');
  const [createdInvite, setCreatedInvite] = useState<CreatedInvitation | null>(null);
  const invite = useMutation({
    mutationFn: () =>
      apiFetch<CreatedInvitation>(`/orgs/${org.orgId}/invitations`, {
        method: 'POST',
        body: { email: inviteEmail.trim(), role: inviteRole },
      }),
    onSuccess: (created) => {
      setCreatedInvite(created);
      setInviteEmail('');
      invalidateAll();
    },
    onError,
  });

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: OrgRole }) =>
      apiFetch(`/orgs/${org.orgId}/members/${userId}`, { method: 'PATCH', body: { role } }),
    onSuccess: () => {
      toast('Role updated.', 'success');
      invalidateAll();
    },
    onError,
  });

  const [memberToRemove, setMemberToRemove] = useState<OrgMember | null>(null);
  const removeMember = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/orgs/${org.orgId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Member removed.', 'success');
      setMemberToRemove(null);
      invalidateAll();
    },
    onError,
  });

  const revokeInvite = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/orgs/${org.orgId}/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: invalidateAll,
    onError,
  });

  const [assessorEmail, setAssessorEmail] = useState('');
  const addAssessor = useMutation({
    mutationFn: () =>
      apiFetch<AssessorAssignment>(`/orgs/${org.orgId}/assessors`, {
        method: 'POST',
        body: { email: assessorEmail.trim() },
      }),
    onSuccess: () => {
      toast('Assessor assigned.', 'success');
      setAssessorEmail('');
      invalidateAll();
    },
    onError,
  });
  const revokeAssessor = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/orgs/${org.orgId}/assessors/${id}`, { method: 'DELETE' }),
    onSuccess: invalidateAll,
    onError,
  });

  if (!isAdmin) {
    return <EmptyState title="Admins only" message="Only organisation admins can manage members." />;
  }
  if (members.isLoading) return <LoadingPane label="Loading members" />;

  const submitInvite = (e: FormEvent) => {
    e.preventDefault();
    invite.mutate();
  };

  return (
    <div>
      <PageHeader
        title="Members"
        subtitle="Invite your team, set roles, and give external assessors access"
      />

      <Card title="Members">
        <Table
          head={
            <>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Sign-in</th>
              <th>Last sign-in</th>
              <th />
            </>
          }
        >
          {(members.data ?? []).map((m) => {
            const self = m.user.id === me?.user.id;
            return (
              <tr key={m.user.id}>
                <td>
                  {m.user.name} {self && <Badge tone="primary">you</Badge>}{' '}
                  {m.status === 'disabled' && <Badge tone="neutral">disabled</Badge>}
                </td>
                <td>{m.user.email}</td>
                <td>
                  <Select
                    aria-label={`Role for ${m.user.name}`}
                    value={m.role}
                    disabled={self}
                    onChange={(e) =>
                      changeRole.mutate({ userId: m.user.id, role: e.target.value as OrgRole })
                    }
                    style={{ width: 150 }}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Select>
                </td>
                <td>{idpLabel(m.idp)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{m.lastLoginAt ? formatDate(m.lastLoginAt) : 'never'}</td>
                <td>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={self}
                    onClick={() => setMemberToRemove(m)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            );
          })}
        </Table>
      </Card>

      <Card title="Invitations">
        <form
          onSubmit={submitInvite}
          style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}
        >
          <div style={{ flex: '1 1 240px' }}>
            <Field label="Email" required>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              )}
            </Field>
          </div>
          <div>
            <Field label="Role">
              {(props) => (
                <Select {...props} value={inviteRole} onChange={(e) => setInviteRole(e.target.value as OrgRole)}>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Button type="submit" loading={invite.isPending}>
              Send invitation
            </Button>
          </div>
        </form>
        {org.plan === 'free' && (
          <p style={{ color: 'var(--color-warning)' }}>
            The free plan includes one user. <Link to="/settings/billing">Upgrade</Link> to invite
            your team.
          </p>
        )}
        {(invitations.data ?? []).length > 0 && (
          <Table
            head={
              <>
                <th>Email</th>
                <th>Role</th>
                <th>Expires</th>
                <th />
              </>
            }
          >
            {(invitations.data ?? []).map((inv) => (
              <tr key={inv.id}>
                <td>{inv.email}</td>
                <td>
                  <Badge>{inv.role}</Badge>
                </td>
                <td>{formatDate(inv.expiresAt)}</td>
                <td>
                  <Button variant="secondary" size="sm" onClick={() => revokeInvite.mutate(inv.id)}>
                    Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card title="Assessors">
        <p style={{ color: 'var(--color-text-muted)' }}>
          Assessors are external users who review finalised responses across the organisations
          they are assigned to. They must have signed in to the platform at least once.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            addAssessor.mutate();
          }}
          style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}
        >
          <div style={{ flex: '1 1 240px' }}>
            <Field label="Assessor email" required>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  required
                  value={assessorEmail}
                  onChange={(e) => setAssessorEmail(e.target.value)}
                />
              )}
            </Field>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Button type="submit" variant="secondary" loading={addAssessor.isPending}>
              Assign assessor
            </Button>
          </div>
        </form>
        {(assessors.data ?? []).length > 0 && (
          <Table
            head={
              <>
                <th>Name</th>
                <th>Email</th>
                <th>Scope</th>
                <th>Assigned</th>
                <th />
              </>
            }
          >
            {(assessors.data ?? []).map((a) => (
              <tr key={a.id}>
                <td>{a.user.name}</td>
                <td>{a.user.email}</td>
                <td>{a.assessmentId ? <Badge>single assessment</Badge> : <Badge tone="info">whole organisation</Badge>}</td>
                <td>{formatDate(a.createdAt)}</td>
                <td>
                  <Button variant="secondary" size="sm" onClick={() => revokeAssessor.mutate(a.id)}>
                    Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Dialog
        open={!!createdInvite}
        onClose={() => setCreatedInvite(null)}
        title="Invitation created"
      >
        <p>
          Share this link with <strong>{createdInvite?.email}</strong>. It is shown once and
          expires {createdInvite ? formatDate(createdInvite.expiresAt) : ''}.
        </p>
        <p>
          <code style={{ wordBreak: 'break-all' }}>{createdInvite?.acceptUrl}</code>
        </p>
        <div className="dialog-actions">
          <Button
            variant="secondary"
            onClick={() => {
              if (createdInvite) void navigator.clipboard.writeText(createdInvite.acceptUrl);
              toast('Link copied to clipboard.', 'success');
            }}
          >
            Copy link
          </Button>
          <Button onClick={() => setCreatedInvite(null)}>Done</Button>
        </div>
      </Dialog>

      <Dialog
        open={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        title="Remove member?"
      >
        <p>
          {memberToRemove?.user.name} ({memberToRemove?.user.email}) will lose access to this
          organisation immediately. Their contributions and audit history are retained.
        </p>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setMemberToRemove(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={removeMember.isPending}
            onClick={() => memberToRemove && removeMember.mutate(memberToRemove.user.id)}
          >
            Remove member
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
